import Vue from 'vue';
import store from '@/store';
import Router from 'vue-router';
import Envelopes from '@/views/Envelopes.vue';
import ContactsView from '@/views/Contacts.vue';
import ContactStatus from '@/views/ContactStatus.vue';
import BrandingFlagGate from '@/components/pages/branding/BrandingFlagGate';
import EmailTemplateEdit from '@/views/EmailTemplateEdit.vue';
import HelpView from '@/views/Help.vue';
import UsersAndTeams from '@/views/UsersAndTeams.vue';
import MyProfile from '@/views/MyProfile.vue';
import NotFound from '@/views/NotFound.vue';
import Login from '@/views/Login.vue';
import Register from '@/views/Register.vue';
import SetupMFA from '@/views/SetupMFA.vue';
import MFA from '@/views/MFA.vue';
import UseRecoveryCode from '@/views/UseRecoveryCode.vue';
import MFARecoveryCode from '@/views/MFARecoveryCode.vue';
import ForgotPassword from '@/views/ForgotPassword.vue';
import ResetPassword from '@/views/ResetPassword.vue';
import RegistrationVerifyEmail from '@/views/RegistrationVerifyEmail.vue';
import external from '@/utils/external';
import i18n from '@/locales';
import { configureSentryScope } from '@/lib/sentry';

// Routes split in to own files
import templateRoutes from '@/router/templates';
import envelopeRoutes from '@/router/envelopes';
import companySettingsRoutes from '@/router/company';
import adminRoutes from '@/router/admin';
import bulkSendRoutes from '@/router/bulkSend';
import createEnvelope from '@/router/createEnvelope';
import integrationsRoutes from '@/router/integrations';

// short-term designs routes
import designRoutes from '@/router/designs';

Vue.use(Router);

const router = new Router({
  mode: 'history',
  routes: [
    {
      path: '/login',
      name: 'Login',
      component: Login,
      meta: {
        title: 'login',
        requiresAuth: false,
        hideAppLevelBanner: true,
      },
    },
    {
      path: '/register',
      name: 'Register',
      component: Register,
      meta: {
        title: 'register',
        requiresAuth: false,
      },
    },
    {
      path: '/registration-verify/:verifyCode',
      name: 'RegistrationVerifyEmail',
      component: RegistrationVerifyEmail,
      meta: {
        title: 'register',
      },
    },
    {
      path: '/envelopes',
      name: 'Envelopes',
      component: Envelopes,
      meta: {
        title: 'envelopes',
        requiresAuth: true,
      },
    },
    {
      path: '/',
      redirect: {
        name: 'Envelopes',
      },
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/library',
      redirect: {
        name: 'Envelopes',
      },
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/library/page',
      redirect: {
        name: 'Envelopes',
      },
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/library/page/:pageNumber',
      redirect(to) {
        return {
            name: 'Envelopes',
            query: {
                page: to.params.pageNumber,
            },
        };
      },
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/contacts',
      name: 'Contacts',
      component: ContactsView,
      meta: {
        title: 'contacts',
        icon: 'address-book',
        requiresAuth: true,
      },
    },
    {
      path: '/contacts/:contactId',
      name: 'ContactStatus',
      component: ContactStatus,
      meta: {
        title: 'contactStatus',
        icon: 'address-book',
        requiresAuth: true,
      },
    },
    {
      path: '/branding',
      name: 'Branding',
      component: BrandingFlagGate,
      meta: {
        title: 'branding',
        icon: 'pencil',
        requiresAuth: true,
      },
    },
    {
      path: '/branding/email/:emailId',
      name: 'EmailTemplateEdit',
      component: EmailTemplateEdit,
      meta: {
        title: 'branding',
        icon: 'pencil',
        requiresAuth: true,
      },
    },
    {
      path: '/users',
      name: 'UsersAndTeams',
      component: UsersAndTeams,
      meta: {
        title: 'usersAndTeams',
        icon: 'users',
        requiresAuth: true,
      },
    },
    {
      path: '/two-factor-authentication/setup',
      name: 'SetupMFA',
      component: SetupMFA,
      meta: {
        title: 'setupMFA',
        requiresAuth: false,
        hideAppLevelBanner: true,
      },
    },
    {
      path: '/two-factor-authentication',
      name: 'MFA',
      component: MFA,
      meta: {
        title: 'MFA',
        requiresAuth: false,
        hideAppLevelBanner: true,
      },
    },
    {
      path: '/two-factor-authentication/recovery-code',
      name: 'MFARecoveryCode',
      component: MFARecoveryCode,
      meta: {
        title: 'MFARecoveryCode',
        requiresAuth: false,
        hideAppLevelBanner: true,
      },
      props: true,
    },
    {
      path: '/two-factor-authentication/use-recovery-code',
      name: 'useRecoveryCode',
      component: UseRecoveryCode,
      meta: {
        title: 'useRecoveryCode',
        requiresAuth: false,
        hideAppLevelBanner: true,
      },
    },
    {
      path: '/help',
      name: 'Help',
      component: HelpView,
      meta: {
        title: 'help',
        icon: 'life-ring',
        requiresAuth: true,
      },
    },
    {
      path: '/my-profile',
      name: 'MyProfile',
      component: MyProfile,
      meta: {
        title: 'myProfile',
        icon: 'user',
        requiresAuth: true,
      },
    },
    {
      path: `/code/${process.env.VUE_APP_TEMP_VOUCHER}`,
      redirect: {
        name: 'Billing',
        query: {
          voucher: process.env.VUE_APP_TEMP_VOUCHER,
        },
      },
    },
    {
      path: '/forgot',
      name: 'ForgotPassword',
      component: ForgotPassword,
      meta: {
        title: 'forgotPassword',
        requiresAuth: false,
      },
    },
    {
      path: '/forgot/:resetToken',
      name: 'ResetPassword',
      component: ResetPassword,
      meta: {
        title: 'resetPassword',
        requiresAuth: false,
      },
    },
    ...integrationsRoutes,
    ...envelopeRoutes,
    ...companySettingsRoutes,
    ...templateRoutes,
    ...adminRoutes,
    ...designRoutes,
    ...bulkSendRoutes,
    ...createEnvelope,
    {
      path: '*',
      name: '404',
      component: NotFound,
      meta: {
        title: 'notFound',
        icon: 'book',
        requiresAuth: false,
      },
    },
  ],
});

router.beforeEach(async (to, from, next) => {
  if (!store.state.initialised) await store.dispatch('INITIALISE_STORE');
  if (to.meta.requiresAuth && !store.getters['auth/isAuthenticated']) { next('/login'); }

  if (to.name !== 'Login') {
    if (store.getters['userMfa/mfaSetupRequired'] && to.name !== 'SetupMFA') {
      return next({ name: 'SetupMFA' });
    }

    if (
      store.getters['userMfa/mfaAuthenticationRequired'] &&
      to.name !== 'MFA' &&
      to.name !== 'useRecoveryCode'
    ) {
      return next({ name: 'MFA' });
    }
  }

  if (to.meta.title) {
    document.title = `${i18n.t(`routeTitles.${to.meta.title}`)} - Signable`;
  } else {
    document.title = 'Signable';
  }
  return next();
});

router.afterEach((to) => {
  const user = store.getters['user/user'];
  const company = store.getters['company/company'];

  external.trackPageview({
      user: { ...user },
      path: to.path,
  });

  if (user?.id) {
    configureSentryScope((scope) => {
      scope.setUser({
        id: user.company_id,
        username: user.name,
        email: user.email,
        full: user,
      });
    });
  }

  // Chameleon Initiate
  if (window.chmln && user?.id) {
    window.chmln.identify(user.id, {
      // eslint-disable-line no-undef
      email: user.email,
      created: user.created_at,
      name: user.name,
      companyEmailVerified: store.getters['company/company'].email_verified,
    });
  }

  // Identify Hubspot
  if (user) {
    external.hubspot.identify({
        signable_user_id: user.id,
        email: user.email,
        firstname: user.firstName,
        lastname: user.lastName,
    });
  }

  // Profitwell
  external.trackEvent('start_pw', user?.email ? { pw_user_email: user.email } : {});

  // Product Fruits
  if (user) {
      const productFruitsData = {
        ...user,
        company,
      };

      external.productFruits.init(productFruitsData);
  }
});

export default router;
